import React, { createContext, useContext, useReducer, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CART':
      return action.payload;
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      } else {
        return {
          ...state,
          items: [...state.items, action.payload],
          brewery_id: action.payload.brewery_id,
          brewery_name: action.payload.brewery_name,
          delivery_config: action.payload.delivery_config // Store delivery config
        };
      }
    case 'REMOVE_ITEM':
      const newItems = state.items.filter(item => item.id !== action.payload);
      return {
        ...state,
        items: newItems,
        // If cart is empty, clear brewery data too
        brewery_id: newItems.length === 0 ? null : state.brewery_id,
        brewery_name: newItems.length === 0 ? null : state.brewery_name,
        delivery_config: newItems.length === 0 ? null : state.delivery_config
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
    case 'CLEAR_CART':
      return {
        items: [],
        brewery_id: null,
        brewery_name: null,
        delivery_config: null,
        delivery_address: null,
        payment_method: null
      };
    case 'SET_DELIVERY_ADDRESS':
      return { ...state, delivery_address: action.payload };
    case 'SET_PAYMENT_METHOD':
      return { ...state, payment_method: action.payload };
    default:
      return state;
  }
};

const initialState = {
  items: [],
  brewery_id: null,
  brewery_name: null,
  delivery_config: null,
  delivery_address: null,
  payment_method: null
};

export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cerveja_delivery_cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        dispatch({ type: 'LOAD_CART', payload: parsedCart });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cerveja_delivery_cart', JSON.stringify(cart));
  }, [cart]);

  const addItem = (product, quantity = 1) => {
    // Check if adding from different brewery
    if (cart.brewery_id && cart.brewery_id !== product.brewery_id && cart.items.length > 0) {
      toast.error('Você só pode adicionar produtos da mesma cervejaria no carrinho', {
        icon: '🚫'
      });
      return;
    }

    const item = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brewery_id: product.brewery_id,
      brewery_name: product.brewery?.name || '',
      delivery_config: product.brewery?.delivery_config, // Pass this down
      quantity
    };

    dispatch({ type: 'ADD_ITEM', payload: item });
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  const removeItem = (productId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
    toast.success('Item removido');
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    toast.success('Carrinho limpo');
  };

  const setDeliveryAddress = (address) => {
    dispatch({ type: 'SET_DELIVERY_ADDRESS', payload: address });
  };

  const setPaymentMethod = (method) => {
    dispatch({ type: 'SET_PAYMENT_METHOD', payload: method });
  };

  const getTotalItems = () => {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getDeliveryFee = () => {
    // Mock delivery fee
    return cart.items.length > 0 ? 5.00 : 0;
  };

  const getFinalTotal = () => {
    return getTotalPrice() + getDeliveryFee();
  };

  const value = {
    cart,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setDeliveryAddress,
    setPaymentMethod,
    getTotalItems,
    getTotalPrice,
    getDeliveryFee,
    getFinalTotal
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};